import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosPromise } from 'axios';
import useAxios from 'axios-hooks';
import {
  AxiosMethod,
  defaultAxiosOptions,
  notification,
  PopupAlertType,
  serviceEndpoints,
  webAppRoutes,
} from '../../constants';
import { usePopupAlertsReducer } from '../../store/reducers';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import { is } from '../../utils/is';
import { UserModel } from '../../models';
import { ResponseValuesOverride } from '../../models/response-values-override';

function useSaveUserEffects(
  { error, data }: ResponseValuesOverride<UserModel>,
  redirectUrl?: string,
) {
  const { showAlert } = usePopupAlertsReducer();
  const navigate = useNavigate();

  useEffect(() => {
    if (!error) return;

    let errMsg = getAxiosErrorMessage(error);
    errMsg = is.string(errMsg) ? errMsg : JSON.stringify(errMsg);

    showAlert({
      variant: PopupAlertType.Error,
      content: errMsg,
    });
  }, [error]);

  useEffect(() => {
    if (!data) return;

    showAlert({
      content: notification.userSuccessfullyDeleted,
    });

    navigate(redirectUrl || webAppRoutes.settings);
  }, [data]);
}

export function useDeleteUser(
  redirectUrl?: string,
): [boolean, (userId: string) => AxiosPromise<UserModel>] {
  const [responseData, execute] = useAxios<UserModel>(
    {},
    { ...defaultAxiosOptions },
  );

  useSaveUserEffects(responseData, redirectUrl);

  return [
    responseData.loading,
    (userId: string) =>
      execute({
        method: AxiosMethod.Delete,
        url: serviceEndpoints.usersApi(userId),
      }),
  ];
}
